import { useEffect, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { Card, Chip, SectionLabel, Num, Unresolved, Columns, Col } from "@coachbrain/ui";
import { CoachShell, ErrorBox, Loading, useMedia } from "../components/common";
import { useLoad } from "../lib/hooks";
import { dateShort, humanPattern } from "../lib/format";
import { en } from "../i18n/en";
import { OverviewBody } from "./overview";

/** Clients: a list on phones; a master/detail pane (list left, selected client's overview right) from 768px. */
export function ClientsRoute() {
  const navigate = useNavigate();
  const { data, error, reload } = useLoad((a) => a.listClients());
  const desktop = useMedia("(min-width: 768px)");
  const [selected, setSelected] = useState<string | null>(null);
  useEffect(() => { if (data && !selected && data[0]) setSelected(data[0].client.id); }, [data, selected]);
  if (error) return <CoachShell><ErrorBox message={error} onRetry={reload} /></CoachShell>;
  if (!data) return <CoachShell><Loading /></CoachShell>;
  const t = en.clients;
  const list = (
    <>
      {data.length === 0 ? <Card><span className="cb-muted">{t.none}</span><Chip tone="navy" onClick={() => void navigate({ to: "/onboarding" })}>{t.addFirst}</Chip></Card> : null}
      {data.map((c) => (
        <Card key={c.client.id} className={desktop && selected === c.client.id ? "cb-card-selected" : ""} onClick={() => { if (desktop) setSelected(c.client.id); else void navigate({ to: `/clients/${c.client.id}` }); }}>
          <div className="cb-row-main"><span className="cb-h3">{c.client.name}{c.client.sample ? <span className="cb-small cb-muted"> · {t.sample}</span> : null}</span>{c.challengeCount ? <span className="cb-chip cb-chip-violet">{c.challengeCount} challenges</span> : null}</div>
          <div className="cb-row-sub">“{c.primaryGoalVerbatim}”</div>
          <div className="cb-inline-actions cb-small cb-muted">{c.lastSessionAt ? <span>{t.lastSession(dateShort(c.lastSessionAt))}</span> : null}{c.openQuestions ? <Unresolved>{t.open(c.openQuestions)}</Unresolved> : null}{c.nextScheduled ? <span>{t.next(c.nextScheduled)}</span> : null}</div>
        </Card>
      ))}
      <Chip tone="ground" onClick={() => void navigate({ to: "/onboarding" })}>{t.add}</Chip>
    </>
  );
  return (
    <CoachShell title={en.nav.clients} subtitle={t.active(data.length)} wide>
      {desktop ? (
        <Columns kind="master-detail">
          <Col area="list">{list}</Col>
          <Col area="detail">{selected ? <OverviewBody key={selected} id={selected} embedded /> : <Card><span className="cb-muted">{t.pick}</span></Card>}</Col>
        </Columns>
      ) : list}
    </CoachShell>
  );
}

export function TodayRoute() {
  const navigate = useNavigate();
  const { data, error, reload } = useLoad((a) => a.today());
  if (error) return <CoachShell><ErrorBox message={error} onRetry={reload} /></CoachShell>;
  if (!data) return <CoachShell><Loading /></CoachShell>;
  return (
    <CoachShell title={en.nav.today} subtitle={data.date}>
      <Columns kind="cards">
        {data.items.length === 0 ? <Card><span className="cb-muted">Nothing scheduled today.</span></Card> : null}
        {data.items.map((it) => (
          <Card key={it.client.id}>
            <div className="cb-row-main"><span className="cb-h3">{it.client.name}</span><Num className="cb-row-right">{it.when ?? ""}</Num></div>
            {it.planTitle ? <div className="cb-row-sub">Planned: {it.planTitle}</div> : null}
            <div className="cb-inline-actions">
              <Chip tone="navy" onClick={() => void navigate({ to: `/clients/${it.client.id}` })}>Overview</Chip>
              <Chip tone="ground" onClick={() => void navigate({ to: `/clients/${it.client.id}/log` })}>Log</Chip>
              {it.pendingReviewId ? <Chip tone="unresolved" onClick={() => void navigate({ to: `/clients/${it.client.id}/review/${it.pendingReviewId}` })}>Review waiting</Chip> : null}
              {it.hasOpenQuestion ? <Chip tone="unresolved">Open question</Chip> : null}
            </div>
          </Card>
        ))}
      </Columns>
    </CoachShell>
  );
}

export function LogPickerRoute() {
  const navigate = useNavigate();
  const { data } = useLoad((a) => a.listClients());
  return (
    <CoachShell title={en.capture.title} subtitle="Who trained?">
      <Columns kind="cards">
        {!data ? <Loading /> : data.map((c) => <Card key={c.client.id} onClick={() => void navigate({ to: `/clients/${c.client.id}/log` })}><span className="cb-h3">{c.client.name}</span></Card>)}
      </Columns>
    </CoachShell>
  );
}

/** Library: a card list on phones; a searchable, filterable table from 768px. */
export function LibraryRoute() {
  const { data, error, reload } = useLoad((a) => a.exercises());
  const desktop = useMedia("(min-width: 768px)");
  const [q, setQ] = useState("");
  const [pattern, setPattern] = useState("");
  if (error) return <CoachShell><ErrorBox message={error} onRetry={reload} /></CoachShell>;
  if (!data) return <CoachShell><Loading /></CoachShell>;
  const t = en.library;
  const patterns = [...new Set(data.map((e) => e.pattern))].sort();
  const rows = data.filter((e) => (!pattern || e.pattern === pattern) && (!q || e.name.toLowerCase().includes(q.toLowerCase()) || (e.aliases ?? []).some((a) => a.toLowerCase().includes(q.toLowerCase()))));
  const filters = (
    <div className="cb-filters">
      <input className="cb-input" aria-label={t.search} placeholder={t.search} value={q} onChange={(e) => setQ(e.target.value)} />
      <select className="cb-select" aria-label={t.columns.pattern} value={pattern} onChange={(e) => setPattern(e.target.value)}><option value="">{t.allPatterns}</option>{patterns.map((p) => <option key={p} value={p}>{humanPattern(p)}</option>)}</select>
      <span className="cb-small cb-muted">{rows.length} / {data.length}</span>
    </div>
  );
  return (
    <CoachShell title={en.nav.library} subtitle={`${data.length} exercises · ${t.readOnly}`} wide>
      {filters}
      {desktop ? (
        <Card className="cb-card-flush" style={{ overflowX: "auto" }}>
          <table className="cb-table">
            <thead><tr><th>{t.columns.name}</th><th>{t.columns.pattern}</th><th>{t.columns.adaptations}</th><th>{t.columns.modality}</th><th>{t.columns.load}</th></tr></thead>
            <tbody>{rows.map((e) => <tr key={e.id}><td className="cb-strong">{e.name}</td><td>{humanPattern(e.pattern)}</td><td>{e.primaryAdaptations.map((a) => humanPattern(a)).join(", ")}</td><td>{e.modality}{e.unilateral ? ` · ${t.unilateral}` : ""}</td><td>{e.loadConvention === "per_hand" ? t.perHand : humanPattern(e.loadConvention)}</td></tr>)}</tbody>
          </table>
        </Card>
      ) : (
        <Card className="cb-card-flush">
          <div><SectionLabel>{t.catalog}</SectionLabel></div>
          {rows.map((e) => <div key={e.id} className="cb-row"><div className="cb-row-main"><span className="cb-row-left" style={{ fontSize: 15 }}>{e.name}</span><span className="cb-small cb-muted">{humanPattern(e.pattern)}</span></div><div className="cb-row-sub">{e.primaryAdaptations.map((a) => humanPattern(a)).join(", ")} · {e.modality}{e.unilateral ? ` · ${t.unilateral}` : ""}{e.loadConvention === "per_hand" ? ` · load ${t.perHand}` : ""}</div></div>)}
        </Card>
      )}
    </CoachShell>
  );
}
